import React, { FC, useCallback } from 'react';
import styled from 'styled-components';
import { Link, To } from 'react-router-dom';
import { textColor, THEME_ALU, THEME_BLACK, THEME_PRIMARY } from '../../styles/basics/colors';
import { BucketEntry } from '../../store/api';
import { EntryPageType } from '../../../models/EntryPage';
import RoutingUtils from '../../shared/utils/utils-routing';
import { ALL_LANGUAGES } from '../language/constants';
import { useTranslation } from 'react-i18next';
import { MULTI_LANGUAGE_SHOP, useFeature } from '../../shared/utils/togglz';
import { useAgeCategorySearchParam } from '../../shared/utils/useAgeCategorySearchParam';

const StLanguageHeader = styled.div`
    font-size: 16px;
    margin-top: 8px;
    margin-bottom: 8px;
`;

const StSidebarItem = styled.li`
    line-height: 18px;

    & a {
        ${textColor(THEME_BLACK)};

        :hover {
            ${textColor(THEME_PRIMARY)};
        }
    }
`;

const StDocCount = styled.span`
    ${textColor(THEME_ALU)};
    font-size: 10px;
    margin-left: 0.3em;
`;

type Props = {
    readonly languages?: ReadonlyArray<BucketEntry>;
    readonly topLanguages?: ReadonlyArray<BucketEntry>;
    readonly pageType?: EntryPageType;
    readonly loanFormatUrl?: string;
    readonly shopLanguage: string | undefined;
};

export const LanguageSidebar: FC<Props> = ({
    languages = [],
    topLanguages = [],
    loanFormatUrl = '',
    pageType,
    shopLanguage
}) => {
    const { t } = useTranslation();
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const { value: ageCategory } = useAgeCategorySearchParam();

    const buildUrl = useCallback(
        (language: BucketEntry) => {
            switch (pageType) {
                case 'LOTE':
                    // PORTAL-3993 use all products link instead of featured link
                    return RoutingUtils.foreignLanguageAllProductsPageLocationDescriptor({
                        languageId: language.key,
                        loanFormat: loanFormatUrl,
                        ageCategory
                    });
                case 'LANGUAGE_LEARNING':
                    // PORTAL-3993 use all products link instead of featured link
                    return RoutingUtils.languageLearningAllProductsPageLocationDescriptor({
                        languageId: language.key,
                        loanFormat: loanFormatUrl,
                        isMultiLanguageShopEnabled,
                        shopLanguage,
                        ageCategory
                    });
                case 'LANGUAGES':
                    return RoutingUtils.languagesPageLocationDescriptor({
                        loanFormat: loanFormatUrl,
                        language: language.value,
                        shopLanguage,
                        isMultiLanguageShopEnabled,
                        ageCategory
                    });
                default:
                    return undefined;
            }
        },
        [pageType, loanFormatUrl, isMultiLanguageShopEnabled, shopLanguage, ageCategory]
    );

    if (languages.length < 1 && topLanguages.length < 1) {
        return null;
    }

    return (
        <>
            {topLanguages.length > 0 ? (
                <>
                    <StLanguageHeader>{t('components.languageSidebar.topLanguages')}</StLanguageHeader>
                    {topLanguages.map(language => (
                        <LanguageSidebarItem key={language.key} language={language} destination={buildUrl(language)} />
                    ))}
                </>
            ) : null}
            {languages.length > 0 ? (
                <>
                    <StLanguageHeader>{t('shop.language.languages')}</StLanguageHeader>
                    {languages.map(language => (
                        <LanguageSidebarItem key={language.key} language={language} destination={buildUrl(language)} />
                    ))}
                </>
            ) : null}
        </>
    );
};

type LanguageSidebarItemProps = {
    readonly language: BucketEntry;
    readonly destination?: To;
};
const LanguageSidebarItem: FC<LanguageSidebarItemProps> = ({ language, destination }) => {
    const { t } = useTranslation();

    if (!destination) {
        return null;
    }

    return (
        <StSidebarItem>
            <Link to={destination}>
                {language.key === ALL_LANGUAGES
                    ? t('allLanguages')
                    : t(`filters:language.${language.key}`, {
                          defaultValue: language.key
                      })}
                <StDocCount>({language.docCount})</StDocCount>
            </Link>
        </StSidebarItem>
    );
};
